import { useMemo } from "react";
import { useAppSelector } from "../redux/hooks";

/**
 * Get principal type from localStorage (set on login).
 */
function getStoredPrincipalType(): "company" | "user" | null {
  if (typeof localStorage === "undefined") return null;
  const stored = localStorage.getItem("principalType");
  if (stored === "company" || stored === "user") return stored;
  return null;
}

/**
 * Central hook for auth state and permission checks.
 *
 * Company principals (type === 'company') bypass every permission check
 * within their own company scope.
 *
 * User principals are checked against the permissions array in the auth slice.
 */
export const useAuth = () => {
  const company = useAppSelector((s) => s.company);
  const auth = useAppSelector((s) => s.auth);

  // Determine principal type from redux state or localStorage
  const storedType = getStoredPrincipalType();
  const principalType = auth.principalType ?? storedType;

  const isCompanyPrincipal = principalType === "company";

  const permissions = useMemo(() => auth.permissions ?? [], [auth.permissions]);
  const permissionSet = useMemo(() => new Set(permissions), [permissions]);

  const user = auth.user;
  const role = auth.role;

  const hasPermission = (key: string): boolean => {
    if (isCompanyPrincipal) return true;
    return permissionSet.has(key);
  };

  const hasAnyPermission = (keys: string[]): boolean =>
    keys.some((k) => hasPermission(k));

  const hasAllPermissions = (keys: string[]): boolean =>
    keys.every((k) => hasPermission(k));

  return {
    isAuthenticated: company.isAuthenticated || auth.isAuthenticated,
    isCompanyPrincipal,
    user,
    role,
    roleType: user?.role?.roleType ?? null,
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
};
